import { track } from '@bnnvara/analytics/src/track';
import { useLazyQuery } from '@apollo/client';
import { FollowButton, BaseButton } from '@bnnvara/buttons';
import { Modal } from '@bnnvara/modal';
import { useAccount, useLogin } from '@bnnvara/accounts';
import React, { useCallback, useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { EditorialNotificationSettings } from '@/components/Profile/NotificationSettings/EditorialNotificationSettings';
import { useNotifications } from '@/hooks/notifications';
import { FetchNotificationSettings } from '@/api/queries/me/notifications.gql';
import { createNotification } from './createNotification';
import { FollowBrandContext } from './FollowBrandContext';
import * as S from './styled';
import { useIsFollowedByUser } from './useIsFollowedByUser';

const FollowBrand = ({ brand }) => {
  const { isLoggedIn } = useLogin();
  const { profile } = useAccount();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { followedBrands } = useContext(FollowBrandContext);
  const { isFollowedByUser, toggle } = useIsFollowedByUser(brand.id);
  const hasSubscriptions = Boolean(followedBrands?.length);
  const { addNotification } = useNotifications();
  const { name, slug } = brand;

  const [getNotificationSettings] = useLazyQuery(FetchNotificationSettings, {
    variables: {
      UID: profile.UID,
    },
    skip: !profile.UID,
    fetchPolicy: 'no-cache',
  });

  const handleClick = useCallback(async () => {
    const result = await toggle();

    if (result === true) {
      addNotification(
        createNotification({
          hasSubscriptions,
          slug: name,
          brandName: slug,
        })
      );
      track('header', 'click', 'volgen');

      const resultNotificationSettings = await getNotificationSettings();

      /** Check if the user wants email notifications and if not show consent dialog */
      const hasNotifyEmailChecked =
        resultNotificationSettings?.data?.me?.notificationSettings
          ?.editorialNotificationPerEmail;

      if (!hasNotifyEmailChecked) setIsModalOpen(true);
    } else if (result === false) {
      track('header', 'click', 'ontvolgen');
    }
  }, [
    addNotification,
    name,
    slug,
    getNotificationSettings,
    hasSubscriptions,
    toggle,
  ]);

  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (isModalOpen && isLoggedIn) {
      getNotificationSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  return (
    <>
      <S.FollowButtonContainer>
        <FollowButton
          onClick={handleClick}
          isFollowedByUser={isFollowedByUser}
          brandThemeName={name}
          followText="Volgen"
          unfollowText="Volgend"
        />
      </S.FollowButtonContainer>

      <Modal onRequestClose={handleCloseModal} isOpen={isModalOpen}>
        <S.InnerModal>
          <S.NotificationContent>
            <EditorialNotificationSettings UID={profile.UID} inModal />
          </S.NotificationContent>
          <S.ButtonGroup>
            <BaseButton
              rel="alternate"
              onClick={handleCloseModal}
              className="cancelButton"
            >
              Bewaren
            </BaseButton>
          </S.ButtonGroup>
        </S.InnerModal>
      </Modal>
    </>
  );
};

FollowBrand.propTypes = {
  brand: PropTypes.shape().isRequired,
};

export default FollowBrand;
